export type ShiftDirection = { x: -1 | 0 | 1; y: -1 | 0 | 1 };

type AudioWindow = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

let context: AudioContext | null = null;
let tickBuffer: AudioBuffer | null = null;

function makeTickBuffer(audioContext: AudioContext) {
  const sampleRate = audioContext.sampleRate;
  const duration = 0.032;
  const buffer = audioContext.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
  const samples = buffer.getChannelData(0);
  const baseFrequency = 680;
  let seed = 0x2f6e2b1;

  for (let index = 0; index < samples.length; index += 1) {
    const time = index / sampleRate;
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const noise = (seed / 0xffffffff) * 2 - 1;
    const attack = Math.min(1, time / 0.0007);
    const decay = Math.exp(-time * 118);
    const tone = Math.sin(2 * Math.PI * baseFrequency * time);
    const body = Math.sin(2 * Math.PI * 235 * time);
    const transient = index < 2 ? 0.8 * (1 - index / 2) : 0;
    const sample = attack * decay * (tone * 0.58 + body * 0.22 + noise * 0.3) + transient;
    samples[index] = Math.tanh(sample * 1.4) * 0.86;
  }

  return buffer;
}

function getContext() {
  const AudioContextClass = window.AudioContext || (window as AudioWindow).webkitAudioContext;
  if (!AudioContextClass) return null;
  if (context?.state === "closed") {
    context = null;
    tickBuffer = null;
  }
  if (!context) {
    context = new AudioContextClass();
    tickBuffer = makeTickBuffer(context);
  }
  return context;
}

function playTick(audioContext: AudioContext, direction: ShiftDirection) {
  if (!tickBuffer) return;
  const source = audioContext.createBufferSource();
  const gain = audioContext.createGain();
  const startsAt = audioContext.currentTime + 0.002;
  source.buffer = tickBuffer;
  gain.gain.setValueAtTime(0.24, startsAt);
  gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + 0.034);

  if (typeof audioContext.createStereoPanner === "function") {
    const panner = audioContext.createStereoPanner();
    panner.pan.setValueAtTime(direction.x * 0.22, startsAt);
    source.connect(gain).connect(panner).connect(audioContext.destination);
  } else {
    source.connect(gain).connect(audioContext.destination);
  }
  source.start(startsAt);
}

export function primeCoverShiftAudio() {
  try {
    const audioContext = getContext();
    if (!audioContext || audioContext.state === "running") return;

    const silentGain = audioContext.createGain();
    const source = audioContext.createBufferSource();
    silentGain.gain.value = 0;
    source.buffer = audioContext.createBuffer(1, 1, audioContext.sampleRate);
    source.connect(silentGain).connect(audioContext.destination);
    source.start();
    void audioContext.resume().catch(() => undefined);
  } catch {
    // The next user gesture gets another chance to unlock Web Audio.
  }
}

export function playCoverShift(direction: ShiftDirection) {
  if (document.hidden) return;
  try {
    const audioContext = getContext();
    if (!audioContext) return;

    // Queue the audible source during the gesture. Safari starts it as soon as
    // the context resumes, instead of losing the gesture while awaiting resume().
    playTick(audioContext, direction);
    if (audioContext.state !== "running") {
      void audioContext.resume().catch(() => undefined);
    }
  } catch {
    // Browsers may deny Web Audio until a later user gesture. Navigation still works.
  }
}
